import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateUserProfile } from '../../Redux/Action/CareSeekerAction';
import Flag from "react-flagkit";
import Contry from "../../Component/Contry";

const ContactEditModal = ({ show, user, onClose, onSuccess, editType = 'alternative' }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: '',
    phone: {
      country_code: '',
      number: ''
    },
    alternative_email: '',
    alternative_phone: {
      country_code: '',
      number: ''
    }
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show && user) {
      setFormData({
        email: user.email || '',
        phone: {
          country_code: user.phone?.country_code || '',
          number: user.phone?.number || ''
        },
        alternative_email: user.alternative_email || '',
        alternative_phone: {
          country_code: user.alternative_phone?.country_code || '',
          number: user.alternative_phone?.number || ''
        }
      });
      setErrors({});
    }
  }, [show, user]);

  const validateForm = () => {
    const newErrors = {};
    
    if (editType === 'primary' || editType === 'all') {
      // Validate primary email (required)
      if (!formData.email || !formData.email.trim()) {
        newErrors.email = 'Primary email is required';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          newErrors.email = 'Please enter a valid email address';
        }
      }

      // Validate primary phone (required)
      if (!formData.phone.country_code || !formData.phone.number) {
        if (!formData.phone.country_code) {
          newErrors.phone_country = 'Country code is required';
        }
        if (!formData.phone.number) {
          newErrors.phone_number = 'Phone number is required';
        }
      } else if (!/^\d+$/.test(formData.phone.number)) {
        newErrors.phone_number = 'Phone number should contain only digits';
      }
    }

    if (editType === 'alternative' || editType === 'all') {
      // Validate alternative email if provided
      if (formData.alternative_email && formData.alternative_email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.alternative_email.trim())) {
          newErrors.alternative_email = 'Please enter a valid email address';
        }
      }

      // Validate alternative phone if provided
      if (formData.alternative_phone.country_code || formData.alternative_phone.number) {
        if (!formData.alternative_phone.country_code) {
          newErrors.alternative_phone_country = 'Country code is required when phone number is provided';
        }
        if (!formData.alternative_phone.number) {
          newErrors.alternative_phone_number = 'Phone number is required when country code is provided';
        } else if (!/^\d+$/.test(formData.alternative_phone.number)) {
          newErrors.alternative_phone_number = 'Phone number should contain only digits';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        userId: user._id
      };

      if (editType === 'primary' || editType === 'all') {
        updateData.email = formData.email.trim();
        updateData.phone = formData.phone;
      }

      if (editType === 'alternative' || editType === 'all') {
        updateData.alternative_email = formData.alternative_email.trim() || null;
        updateData.alternative_phone = (formData.alternative_phone.country_code && formData.alternative_phone.number) 
          ? formData.alternative_phone 
          : null;
      }

      await dispatch(updateUserProfile(updateData));
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating contact information:', error);
      setErrors({ submit: 'Failed to update contact information' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const getModalTitle = () => {
    switch (editType) {
      case 'primary': return 'Edit Primary Contact';
      case 'alternative': return 'Edit Alternative Contact';
      case 'all': return 'Edit Contact Information';
      default: return 'Edit Contact';
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>{getModalTitle()}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Primary Contact Section */}
          {(editType === 'primary' || editType === 'all') && (
            <div style={{ marginBottom: '30px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '16px' }}>Primary Contact Information</h4>
              
              {/* Primary Email */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold', 
                  color: '#333' 
                }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter primary email address"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${errors.email ? '#dc3545' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.email && (
                  <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Primary Phone */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold', 
                  color: '#333' 
                }}>
                  Phone Number *
                </label>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* Country Code */}
                  <div style={{ flex: '0 0 120px' }}>
                    <input
                      type="text"
                      value={formData.phone.country_code}
                      onChange={(e) => handleInputChange('phone.country_code', e.target.value)}
                      placeholder="e.g., +1"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${errors.phone_country ? '#dc3545' : '#ddd'}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    {errors.phone_country && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.phone_country}
                      </div>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={formData.phone.number}
                      onChange={(e) => handleInputChange('phone.number', e.target.value)}
                      placeholder="Enter phone number"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${errors.phone_number ? '#dc3545' : '#ddd'}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    {errors.phone_number && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.phone_number}
                      </div>
                    )}
                  </div>
                </div>

                {/* Show current primary phone with flag if exists */}
                {formData.phone.country_code && (
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '12px', 
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Flag country={Contry(formData.phone.country_code)} size={16} />
                    Preview: {formData.phone.country_code} {formData.phone.number}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Alternative Contact Section */}
          {(editType === 'alternative' || editType === 'all') && (
            <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #e3f2fd' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#1976d2', fontSize: '16px' }}>Alternative Contact Information</h4>
              
              {/* Alternative Email */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold', 
                  color: '#333' 
                }}>
                  Alternative Email
                </label>
                <input
                  type="email"
                  value={formData.alternative_email}
                  onChange={(e) => handleInputChange('alternative_email', e.target.value)}
                  placeholder="Enter alternative email address"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${errors.alternative_email ? '#dc3545' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.alternative_email && (
                  <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                    {errors.alternative_email}
                  </div>
                )}
              </div>

              {/* Alternative Phone */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold', 
                  color: '#333' 
                }}>
                  Alternative Phone
                </label>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* Country Code */}
                  <div style={{ flex: '0 0 120px' }}>
                    <input
                      type="text"
                      value={formData.alternative_phone.country_code}
                      onChange={(e) => handleInputChange('alternative_phone.country_code', e.target.value)}
                      placeholder="e.g., +1"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${errors.alternative_phone_country ? '#dc3545' : '#ddd'}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    {errors.alternative_phone_country && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.alternative_phone_country}
                      </div>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={formData.alternative_phone.number}
                      onChange={(e) => handleInputChange('alternative_phone.number', e.target.value)}
                      placeholder="Enter phone number"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${errors.alternative_phone_number ? '#dc3545' : '#ddd'}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    {errors.alternative_phone_number && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.alternative_phone_number}
                      </div>
                    )}
                  </div>
                </div>

                {/* Show current alternative phone with flag if exists */}
                {formData.alternative_phone.country_code && (
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '12px', 
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Flag country={Contry(formData.alternative_phone.country_code)} size={16} />
                    Preview: {formData.alternative_phone.country_code} {formData.alternative_phone.number}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div style={{
              color: '#dc3545',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              padding: '8px 12px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {errors.submit}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: loading ? 0.7 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: loading ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactEditModal;